/*
 * Jersey personalization block — metafield-driven add-ons.
 *
 * UI (unchanged): 3 tabs Blanko / Spielername / Wunschname + live back-image overlay.
 * DATA/LOGIC: add-ons are hidden PRODUCTS whose variants carry price +
 * inventory. Players come from the Spieler product's variants (rendered server-side as
 * dropdown options with data-*), custom name from the Name product's variant, custom
 * number as one full-number Ziffer variant. Prices are the
 * variant prices; stock disables options. On add-to-cart we post the jersey + one line
 * per selected add-on variant, all sharing a random `_TRIKOT` id.
 *
 * The block takes over the section's submit button (removes `Sd_addProduct` so the
 * theme's cart interceptors ignore it) and handles the submit itself.
 */
(function () {
  'use strict';

  var moneyFmt = null;

  function formatMoney(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, moneyFmt || window.moneyFormat);
      }
    } catch (e) { }
    return 'CHF ' + (Number(cents) / 100).toFixed(2);
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------- personalization handed over from the configurator ----------
   * The configurator's jersey switcher parks what was typed under this key before it swaps
   * jerseys; when its section fetch fails it navigates to the sibling's product page — i.e.
   * here — so this block picks the hand-off up as well and the entry is never lost. Same key
   * and same shape as jersey-configurator-section.js. Read-and-delete (one-shot).
   */
  var CARRY_KEY = 'evz:jersey:carry';
  function takeCarry() {
    try {
      var raw = window.sessionStorage.getItem(CARRY_KEY);
      window.sessionStorage.removeItem(CARRY_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  // The roster is a per-jersey hidden product, so a player's variant id from the previous
  // jersey means nothing here — the hand-off carries the identity (name + number) instead.
  function normName(v) {
    return String(v == null ? '' : v).toLowerCase().replace(/[.,'’`\-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  }
  function digitsOnly(v) { return String(v == null ? '' : v).replace(/\D/g, ''); }

  // The flocked name reaches Conte Sports exactly as stored (FLOCK / Individuell - Name), so
  // store it the way it is printed: uppercase. de-CH maps ä/ö/ü -> Ä/Ö/Ü and keeps i -> I
  // (no Turkish dotted İ); ß -> SS, the Swiss spelling. Digits, spaces, hyphens and
  // apostrophes are left as they are. Same helper in jersey-configurator-section.js.
  function flockCase(v) {
    v = String(v == null ? '' : v);
    if (v.normalize) v = v.normalize('NFC'); // pasted from a Mac: u + combining ¨ -> one ü
    try { return v.toLocaleUpperCase('de-CH'); } catch (e) { return v.toUpperCase(); }
  }
  // Uppercase a name field in place (typing, paste, autofill), keeping the caret put.
  function upcaseField(el) {
    var v = el.value, up = flockCase(v);
    if (up === v) return;
    var pos = el.selectionStart;
    el.value = up;
    if (pos != null && document.activeElement === el) {
      var p = flockCase(v.slice(0, pos)).length;
      try { el.setSelectionRange(p, p); } catch (e) { }
    }
  }

  function init(root) {
    if (root.hasAttribute('data-jp-initialized')) return;
    root.setAttribute('data-jp-initialized', 'true');

    var configEl = root.querySelector('[data-jp-config]');
    if (!configEl) return;
    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    config.numbers = config.numbers || {};

    if (!moneyFmt) {
      moneyFmt = window.moneyFormat || config.moneyFormat || 'CHF {{amount}}';
    }

    var strings = config.strings || {};
    function jpText(key, fallback) {
      return strings[key] || fallback;
    }

    var section = root.closest('.shopify-section');
    var productInfo = root.closest('product-info') || section || document;
    var form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
    var submitBtn = form ? form.querySelector('button[type="submit"]') : null;

    var state = {
      mode: 'none',
      player: null, // { variantId, name, number, amount, sku }
      customName: '',
      customNumber: '',
      variantPrice: Number(config.initialVariantPrice) || 0,
      busy: false
    };

    var tabs = root.querySelectorAll('.personalization-tab');
    var playerContainer = root.querySelector('[data-jp-player-container]');
    var customInputs = root.querySelector('[data-jp-custom-inputs]');
    var nameInput = root.querySelector('[data-jp-name]');
    var numberInput = root.querySelector('[data-jp-number]');
    var customError = root.querySelector('[data-jp-custom-error]');
    var selectWrapper = root.querySelector('[data-jp-select-wrapper]');
    var selectTrigger = root.querySelector('[data-jp-select-trigger]');
    var selectValue = root.querySelector('[data-jp-select-value]');
    var selectOptions = root.querySelector('[data-jp-select-options]');
    var searchInput = root.querySelector('[data-jp-search]');
    var defaultSelectLabel = selectValue
      ? selectValue.textContent
      : jpText('selectPlayer', 'Spieler auswählen');

    /* ---------- live back preview ---------- */
    var preview = setupPreview(productInfo || section || document, config.overlay, config.bluePrint, config.retro);

    /* ---------- helpers: is a personalization present, and its add-on cost ---------- */
    function sanitizeNumber(num) {
      return String(num || '').replace(/\D/g, '').slice(0, 2);
    }
    // The whole number is ONE Ziffer variant (e.g. "99"). Match the
    // typed value to a variant key (also try the leading-zero-stripped form).
    function numberInfo(num) {
      num = sanitizeNumber(num);
      if (!num) return null;
      return config.numbers[num] || config.numbers[String(parseInt(num, 10))] || null;
    }
    // Returns an error string if the typed number has no variant / is out of stock.
    function numberIssue(num) {
      if (!sanitizeNumber(num)) return '';
      var info = numberInfo(num);
      if (!info || info.available === false) {
        return jpText('errorNumberStock', 'Diese Nummer ist nicht verfügbar.');
      }
      return '';
    }
    function isPersonalized() {
      if (state.mode === 'player') return !!state.player;
      if (state.mode === 'custom')
        return !!(state.customName.trim() || state.customNumber.trim());
      return false;
    }
    function addonCents() {
      if (state.mode === 'player') return state.player ? Number(state.player.amount) || 0 : 0;
      if (state.mode === 'custom') {
        var total = 0;
        // Name part: the typed name is charged; if left empty and a "no name" placeholder
        // exists, that is charged instead (so name is always paid for — flat CHF 20 rule).
        if (state.customName.trim() && config.nameAddon) total += Number(config.nameAddon.price) || 0;
        else if (config.noNameAddon) total += Number(config.noNameAddon.price) || 0;
        // Number part: the typed number, else the "no number" placeholder.
        var ni = numberInfo(state.customNumber);
        if (ni) total += Number(ni.price) || 0;
        else if (config.noNumberAddon) total += Number(config.noNumberAddon.price) || 0;
        return total;
      }
      return 0;
    }

    function refreshPreview() {
      if (state.mode === 'player') {
        preview.update(
          state.player && state.player.name,
          state.player && state.player.number,
          !!state.player
        );
      } else if (state.mode === 'custom') {
        var n = state.customName.trim();
        var num = state.customNumber.trim();
        preview.update(n, num, !!(n || num));
      } else {
        preview.update('', '', false);
      }
      applyPrice();
    }

    /* ---------- price ---------- */
    function priceEls() {
      return section ? section.querySelectorAll('[data-product-price]') : [];
    }
    function staffBase() {
      // Apply the aico staff/customer discount to the jersey base price, if any.
      var mult = (config && typeof config.staffDiscountMultiplier === 'number') ? config.staffDiscountMultiplier : 1;
      var base = state.variantPrice;
      if (mult && mult !== 1) {
        base = base * mult;
        // Swiss Rappen rounding to nearest 5 cents (matches customer-price.liquid).
        base = Math.round(base / 5) * 5;
      }
      return base;
    }
    function applyPrice() {
      // Add-ons (name/number) are separate products and stay at full price.
      var total = staffBase() + (isPersonalized() ? addonCents() : 0);
      var text = formatMoney(total);
      var els = priceEls();
      for (var i = 0; i < els.length; i++) els[i].textContent = text;
    }
    function onVariantChange(evt) {
      var variant = evt && evt.data && evt.data.variant;
      if (variant && typeof variant.price === 'number') state.variantPrice = variant.price;
      window.requestAnimationFrame(applyPrice);
    }
    try {
      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
      }
    } catch (e) { }

    /* ---------- tabs ---------- */
    function setMode(mode) {
      state.mode = mode;
      for (var i = 0; i < tabs.length; i++) {
        tabs[i].classList.toggle('active', tabs[i].getAttribute('data-mode') === mode);
      }
      if (playerContainer) playerContainer.classList.toggle('jp-hidden', mode !== 'player');
      if (customInputs) customInputs.classList.toggle('jp-hidden', mode !== 'custom');

      if (mode === 'none') {
        state.player = null;
        state.customName = '';
        state.customNumber = '';
        if (nameInput) nameInput.value = '';
        if (numberInput) numberInput.value = '';
        resetDropdown();
      } else if (mode === 'player') {
        state.customName = '';
        state.customNumber = '';
        if (nameInput) nameInput.value = '';
        if (numberInput) numberInput.value = '';
      } else if (mode === 'custom') {
        state.player = null;
        resetDropdown();
      }
      setCustomError('');
      refreshPreview();
      clearError();
    }

    for (var t = 0; t < tabs.length; t++) {
      (function (tab) {
        tab.addEventListener('click', function () {
          setMode(tab.getAttribute('data-mode'));
        });
      })(tabs[t]);
    }

    /* ---------- player dropdown ---------- */
    // The roster product's variant order is whatever the ERP pushed, so the list came out
    // unsorted and differed per jersey. Order it by jersey number, low to high; entries
    // without a usable number keep their roster order at the end.
    (function sortPlayerOptions() {
      if (!selectOptions) return;
      var opts = selectOptions.querySelectorAll('.custom-select-option');
      var rows = [];
      for (var i = 0; i < opts.length; i++) {
        var n = parseInt((opts[i].getAttribute('data-number') || '').replace(/\D/g, ''), 10);
        rows.push({ el: opts[i], no: isNaN(n) ? 1e9 : n, i: i });
      }
      rows.sort(function (a, b) {
        return a.no - b.no || a.i - b.i;
      });
      for (var j = 0; j < rows.length; j++) selectOptions.appendChild(rows[j].el);
    })();

    function resetDropdown() {
      if (selectValue) selectValue.textContent = defaultSelectLabel;
      if (selectWrapper) selectWrapper.classList.remove('open');
      if (selectOptions) {
        var opts = selectOptions.querySelectorAll('.custom-select-option');
        for (var i = 0; i < opts.length; i++) opts[i].classList.remove('selected');
      }
    }

    if (selectTrigger && selectWrapper) {
      selectTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        selectWrapper.classList.toggle('open');
        if (selectWrapper.classList.contains('open') && searchInput) {
          searchInput.value = '';
          var opts = selectOptions.querySelectorAll('.custom-select-option');
          for (var i = 0; i < opts.length; i++) opts[i].style.display = '';
          setTimeout(function () {
            searchInput.focus();
          }, 50);
        }
      });
    }

    if (searchInput && selectOptions) {
      searchInput.addEventListener('click', function (e) {
        e.stopPropagation();
      });
      searchInput.addEventListener('input', function () {
        var q = searchInput.value.toLowerCase();
        var opts = selectOptions.querySelectorAll('.custom-select-option');
        for (var i = 0; i < opts.length; i++) {
          var txt = (
            (opts[i].getAttribute('data-number') || '') +
            ' ' +
            (opts[i].getAttribute('data-name') || '')
          ).toLowerCase();
          opts[i].style.display = txt.indexOf(q) > -1 ? '' : 'none';
        }
      });
    }

    // Single path into "a player is chosen", shared by the click handler and the restore
    // below, so a handed-over player lands in exactly the state a click would produce.
    function selectPlayerOption(opt) {
      if (!opt || opt.getAttribute('data-available') === 'false') return false;
      state.player = {
        variantId: opt.getAttribute('data-variantid'),
        // Roster names come from the ERP in mixed case ("Stadler") — store them as flocked.
        name: flockCase(opt.getAttribute('data-name')),
        // ERP numbers can carry a "#" prefix (e.g. "#21"); the jersey + cart
        // should use the bare digits.
        number: (opt.getAttribute('data-number') || '').replace(/[^0-9]/g, ''),
        amount: Number(opt.getAttribute('data-amount')) || 0,
        sku: opt.getAttribute('data-sku') || ''
      };
      if (selectValue) {
        selectValue.textContent = state.player.number + ' - ' + state.player.name.toUpperCase();
      }
      if (selectOptions) {
        var opts = selectOptions.querySelectorAll('.custom-select-option');
        for (var i = 0; i < opts.length; i++) opts[i].classList.remove('selected');
      }
      opt.classList.add('selected');
      if (selectWrapper) selectWrapper.classList.remove('open');
      refreshPreview();
      clearError();
      return true;
    }

    if (selectOptions) {
      selectOptions.addEventListener('click', function (e) {
        if (e.target.closest('.custom-select-search')) {
          e.stopPropagation();
          return;
        }
        var opt = e.target.closest('.custom-select-option');
        if (!opt) return;
        // Out-of-stock players are not selectable.
        if (opt.getAttribute('data-available') === 'false') {
          e.stopPropagation();
          return;
        }
        selectPlayerOption(opt);
      });
    }

    document.addEventListener('click', function (e) {
      if (selectWrapper && !selectWrapper.contains(e.target)) {
        selectWrapper.classList.remove('open');
      }
    });

    /* ---------- custom inputs ---------- */
    function setCustomError(msg) {
      if (!customError) return;
      customError.textContent = msg || '';
      customError.style.display = msg ? '' : 'none';
    }
    // Mid-composition (dead keys, Android keyboards) the field is left alone — rewriting it
    // there duplicates text — and compositionend / change uppercase it afterwards. The state
    // is uppercased either way, so preview and cart never see mixed case.
    function onNameInput(e) {
      if (!(e && e.isComposing)) upcaseField(nameInput);
      state.customName = flockCase(nameInput.value);
      refreshPreview();
      clearError();
    }
    if (nameInput) {
      ['input', 'compositionend', 'change'].forEach(function (ev) {
        nameInput.addEventListener(ev, onNameInput);
      });
    }
    if (numberInput) {
      numberInput.addEventListener('input', function () {
        // digits only
        var cleaned = numberInput.value.replace(/\D/g, '').slice(0, 2);
        if (cleaned !== numberInput.value) numberInput.value = cleaned;
        state.customNumber = cleaned;
        setCustomError(numberIssue(cleaned));
        refreshPreview();
        clearError();
      });
    }

    /* ---------- errors (theme's product error slot) ---------- */
    function errorEl() {
      return section ? section.querySelector('.productErrors') : null;
    }
    function showError(msg) {
      var el = errorEl();
      if (el) {
        el.textContent = msg;
        el.style.display = '';
        el.style.visibility = 'visible';
        // Make sure the error is actually in view (it can sit below the fold).
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { }
      } else {
        window.alert(msg);
      }
    }
    function clearError() {
      var el = errorEl();
      if (el) {
        el.textContent = '';
        el.style.display = 'none';
      }
    }

    /* ---------- add to cart ---------- */
    function setBusy(busy) {
      state.busy = busy;
      if (!submitBtn) return;
      submitBtn.disabled = busy;
      submitBtn.classList.toggle('is-loading', busy);
    }

    function postItems(items) {
      return fetch(cartRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error(
              (data && data.description) || jpText('errorCouldNotAdd', 'Could not add to cart')
            );
          });
        }
        return res.json();
      });
    }

    function updateCartCount(cart) {
      try {
        var count =
          typeof window.getDisplayItemCount === 'function'
            ? window.getDisplayItemCount(cart)
            : cart.item_count;
        var badges = document.querySelectorAll('[data-cart-count]');
        for (var i = 0; i < badges.length; i++) {
          var numEl = badges[i].querySelector('.cart-count-badge__number') || badges[i];
          if (count > 0) {
            badges[i].classList.remove('cart-count-badge--hidden');
            numEl.textContent = count < 100 ? count : '';
          } else {
            badges[i].classList.add('cart-count-badge--hidden');
            numEl.textContent = '';
          }
        }
      } catch (e) { }
    }

    function refreshCart() {
      var $ = window.jQuery;
      if ($) {
        var trigger = $('.openCartDrawer').first();
        if (trigger.length) {
          trigger.trigger('click');
          return Promise.resolve();
        }
      }
      return fetch(cartRoot() + 'cart.js')
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          updateCartCount(cart);
          try {
            if (typeof window.buildCart === 'function' && $ && $('[data-side-drawer]').length) {
              $('body')
                .find('[data-side-drawer]')
                .attr('class', 'yv_side_drawer_wrapper')
                .attr('id', 'mini__cart')
                .addClass('mini_cart');
              $('body').addClass('side_Drawer_open');
              window.buildCart(cart, true);
              return;
            }
          } catch (e) { }
          window.location.href = cartRoot() + 'cart';
        });
    }

    function currentVariantId() {
      if (form && form.elements && form.elements['id'] && form.elements['id'].value) {
        return form.elements['id'].value;
      }
      return config.initialVariantId;
    }

    function jerseyTypeValue() {
      var v = (config.version || '').toString().trim();
      if (!v) return '';
      return v.charAt(0).toUpperCase() + v.slice(1);
    }
    function genderValue() {
      var g = (config.gender || '').toString().toLowerCase();
      if (!g) return '';
      if (g.indexOf('frau') > -1 || g.indexOf('women') > -1 || g.indexOf('female') > -1) return 'Frauen';
      if (g.indexOf('kind') > -1 || g.indexOf('kid') > -1) return 'Kinder';
      if (g.indexOf('herr') > -1 || g.indexOf('männ') > -1 || g.indexOf('mann') > -1 || g.indexOf('men') > -1) {
        return 'Männer';
      }
      return config.gender;
    }

    // Cart line items with the exact line-item properties (no more, no less):
    //   add-on line: { _TRIKOT, SKU?, SKU_Nummer?, FLOCK? }                             quantity 1
    //   main  line:  { _TRIKOT, Badges?, _bundle_type:'main', _jersey_type?, _gender?,
    //                  custom mode: 'Individuell - Nummer'?, 'Individuell - Name'?
    //                  player mode: FLOCK? }                                            quantity 1
    // Only non-empty properties are sent; add-on lines first, main jersey line last.
    function buildItems(trikotId, variantId) {
      var items = [];

      var name = '';
      var number = '';
      if (state.mode === 'player' && state.player) {
        name = state.player.name;
        number = state.player.number;
      } else if (state.mode === 'custom') {
        name = state.customName.trim();
        number = state.customNumber.trim();
      }
      name = flockCase(name); // last line of defence: every FLOCK / Individuell - Name below is uppercase
      var flockStr = number && name ? number + ' ' + name : number || name;

      // ---- add-on lines (child of the bundle) ----
      // `_bundle_type: child` is an extra so the cart can group
      // add-ons under the jersey; the ERP reads only _TRIKOT / SKU / FLOCK.
      if (state.mode === 'player' && state.player) {
        var p = state.player;
        var pp = { _TRIKOT: trikotId, _bundle_type: 'child' };
        if (p.sku) pp.SKU = p.sku;
        var pnum = numberInfo(p.number);
        if (pnum && pnum.sku) pp.SKU_Nummer = pnum.sku;
        if (flockStr) pp.FLOCK = flockStr;
        items.push({ id: p.variantId, quantity: 1, properties: pp });
      } else if (state.mode === 'custom') {
        // NAME line: the typed name (real variant + FLOCK). If empty, fall back to the
        // "no name" placeholder variant so the CHF 10 is always charged. `_placeholder` is
        // a private property (hidden by Shopify) that lets the cart identify the phantom.
        if (name && config.nameAddon) {
          var np = { _TRIKOT: trikotId, _bundle_type: 'child' };
          if (config.nameAddon.sku) np.SKU = config.nameAddon.sku;
          np.FLOCK = name;
          items.push({ id: config.nameAddon.variantId, quantity: 1, properties: np });
        } else if (config.noNameAddon) {
          var npn = { _TRIKOT: trikotId, _bundle_type: 'child', _placeholder: 'name' };
          if (config.noNameAddon.sku) npn.SKU = config.noNameAddon.sku;
          items.push({ id: config.noNameAddon.variantId, quantity: 1, properties: npn });
        }
        // NUMBER line: the typed digit variant. If empty, fall back to the "no number"
        // placeholder variant (again always CHF 10).
        var ninfo = numberInfo(number);
        if (ninfo) {
          // The whole number is ONE line item (its own Ziffer variant).
          var dp = { _TRIKOT: trikotId, _bundle_type: 'child' };
          if (ninfo.sku) dp.SKU = ninfo.sku;
          dp.FLOCK = number;
          items.push({ id: ninfo.variantId, quantity: 1, properties: dp });
        } else if (config.noNumberAddon) {
          var dpn = { _TRIKOT: trikotId, _bundle_type: 'child', _placeholder: 'number' };
          if (config.noNumberAddon.sku) dpn.SKU = config.noNumberAddon.sku;
          items.push({ id: config.noNumberAddon.variantId, quantity: 1, properties: dpn });
        }
      }

      // ---- main jersey line (last) ----
      var mp = { _TRIKOT: trikotId };
      if (state.mode === 'custom') {
        // Custom orders show `Individuell - Nummer` / `Individuell - Name`; a main-line
        // FLOCK here would just duplicate them in the WMS "Offen" view (the child add-on
        // lines already carry FLOCK for finishing), so omit it.
        if (number) mp['Individuell - Nummer'] = number;
        if (name) mp['Individuell - Name'] = name;
      } else if (flockStr) {
        // Player mode: the main line has no Individuell fields, so FLOCK labels it.
        mp.FLOCK = flockStr;
      }
      mp._bundle_type = 'main';
      var jt = jerseyTypeValue();
      if (jt) mp._jersey_type = jt;
      var gv = genderValue();
      if (gv) mp._gender = gv;
      // Staff/customer discount (aico): mark the jersey line with the discounted
      // unit price (currency units, e.g. "160.30") so the cart + checkout reroute
      // apply it. Only when a discount actually lowers the base price.
      var jerseyDiscBase = staffBase();
      if (jerseyDiscBase < state.variantPrice) {
        mp._price_item_discount = (jerseyDiscBase / 100).toFixed(2);
      }
      items.push({ id: variantId, quantity: 1, properties: mp });

      return items;
    }

    function handleSubmit(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (state.busy) return;

      // Validation.
      if (state.mode === 'player' && !state.player) {
        showError(jpText('errorSelectPlayer', 'Bitte Spieler auswählen.'));
        return;
      }
      if (state.mode === 'custom') {
        var n = state.customName.trim();
        var num = state.customNumber.trim();
        if (!n && !num) {
          showError(jpText('errorNameOrNumber', 'Bitte Name oder Nummer eingeben.'));
          return;
        }
        var issue = numberIssue(num);
        if (issue) {
          setCustomError(issue);
          showError(issue);
          return;
        }
      }

      clearError();
      setBusy(true);

      // Always bundle the jersey line (even Blanko) under a shared numeric
      // _TRIKOT id; add-on lines share the same id.
      var variantId = currentVariantId();
      var trikotId = String(Math.floor(Math.random() * 100000000));
      var items = buildItems(trikotId, variantId);

      postItems(items)
        .then(function () {
          return refreshCart();
        })
        .catch(function (err) {
          showError((err && err.message) || jpText('errorAddToCart', 'Fehler beim Hinzufügen zum Warenkorb.'));
        })
        .then(function () {
          setBusy(false);
        });
    }

    if (form && submitBtn) {
      submitBtn.classList.remove('Sd_addProduct');
      submitBtn.classList.add('jp-atc-btn');
      form.addEventListener('submit', handleSubmit);
    }

    /* ---------- initial state: hand-off from the configurator, else Blanko ---------- */
    function hasTab(mode) {
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].getAttribute('data-mode') === mode) return true;
      }
      return false;
    }
    // Same player on this jersey: match on name, preferring the entry that also carries the
    // same number. Never match on number alone — if this roster gives #18 to somebody else,
    // that would silently put a different player's name on the shopper's jersey.
    function findPlayerOption(p) {
      if (!selectOptions || !p) return null;
      var wantName = normName(p.name);
      if (!wantName) return null;
      var wantNo = digitsOnly(p.number);
      var opts = selectOptions.querySelectorAll('.custom-select-option');
      var byName = null;
      for (var i = 0; i < opts.length; i++) {
        var o = opts[i];
        if (o.getAttribute('data-available') === 'false') continue;
        if (normName(o.getAttribute('data-name')) !== wantName) continue;
        if (wantNo && digitsOnly(o.getAttribute('data-number')) === wantNo) return o;
        if (!byName) byName = o;
      }
      return byName;
    }
    function restore(c) {
      var mode = c && (c.mode === 'player' || c.mode === 'custom') ? c.mode : 'none';
      // A jersey without that add-on product has no such tab — fall back to Blanko.
      if (!hasTab(mode)) mode = 'none';
      setMode(mode); // clears inputs, sets tab/containers, then we fill them
      if (mode === 'custom') {
        if (nameInput && c.name) {
          var max = parseInt(nameInput.getAttribute('maxlength'), 10) || 0;
          nameInput.value = flockCase(max > 0 ? String(c.name).slice(0, max) : String(c.name));
          state.customName = nameInput.value;
        }
        if (numberInput && c.number) {
          var n = sanitizeNumber(c.number);
          numberInput.value = n;
          state.customNumber = n;
          // This jersey has its own Ziffer variants: flag an unavailable number right away,
          // exactly as typing it would (add-to-cart rejects it on the same check).
          setCustomError(numberIssue(n));
        }
        refreshPreview();
      } else if (mode === 'player' && c.player) {
        // No match (not on this roster, or sold out here) leaves the dropdown on its
        // placeholder, so the shopper sees they have to pick again instead of the wrong name.
        selectPlayerOption(findPlayerOption(c.player));
      }
    }
    restore(takeCarry());
  }

  /*
   * Live preview: overlays the entered name/number (retro bubble font) onto the
   * product's BACK gallery image, switches the gallery to it, and stays locked to the
   * hover-zoom. Back image = media alt starting a word with back/rück/hinten/rear, else
   * 2nd image. The word-start guard matters: photo filenames like
   * "260804_EVZ_Packshots-143_gedruckt" contain "ruck" inside "gedruckt", which used to
   * match every image and pin the preview to the front shot.
   * The FRONT shot (front/vorne match, else the first non-back image) gets the number only.
   */
  function setupPreview(scope, overlayCal, bluePrint, retro) {
    var noop = { update: function () { } };
    var track = scope.querySelector('.evg-main-track');
    if (!track) return noop;
    var items = Array.prototype.slice.call(track.querySelectorAll('.evg-main-item'));
    if (!items.length) return noop;

    function itemText(it) {
      var t = (it.getAttribute('variant-color') || '').toLowerCase();
      var a = it.querySelector('a[aria-label]');
      if (a) t += ' ' + (a.getAttribute('aria-label') || '').toLowerCase();
      var img = it.querySelector('img');
      if (img) t += ' ' + (img.getAttribute('alt') || '').toLowerCase();
      return t;
    }
    function looksBack(it) {
      return /(^|[^a-zäöüß])(back|r[uü]ck|rueck|hinten|rear)/.test(itemText(it));
    }
    function looksFront(it) {
      return /(^|[^a-zäöüß])(front|vorne|vorder)/.test(itemText(it));
    }
    var backItem = items.filter(looksBack)[0] || items[1] || items[0];
    if (!backItem) return noop;
    // Never reuse the back item — an alt naming both sides would stack two overlays on it.
    var notBack = items.filter(function (it) { return it !== backItem; });
    var frontItem = notBack.filter(looksFront)[0] || notBack[0] || null;

    var ovc = overlayCal || {};

    function slideHost(item) {
      return (
        item.querySelector('.image-wrapper') ||
        item.querySelector('.product-slider-item-wrapper') ||
        item
      );
    }
    // The gallery thumb mirrors its slide via data-image="<slide id>" (see selectBack).
    function thumbHost(item) {
      return item.id ? scope.querySelector('.evg-thumb[data-image="' + item.id + '"]') : null;
    }

    // Pins an overlay over one host; slides also stay locked to the image's hover-zoom.
    function buildOverlay(host, side, isThumb) {
      var ov = document.createElement('div');
      ov.className =
        'jp-preview-overlay' +
        (bluePrint ? ' jp-blue-print' : '') +
        (retro ? ' jp-retro' : '') +
        (isThumb ? ' jp-pv-thumb' : '');
      var nEl = null;
      if (side === 'back') {
        nEl = document.createElement('div');
        nEl.className = 'jp-pv-name';
        ov.appendChild(nEl);
      }
      var numEl = document.createElement('div');
      numEl.className = 'jp-pv-number' + (side === 'front' ? ' jp-pv-front-number' : '');
      ov.appendChild(numEl);

      // Per-jersey calibration; *Left nudges off-centre photos (default 0 = centred).
      if (side === 'back') {
        if (ovc.nameTop) ov.style.setProperty('--jp-name-top', ovc.nameTop);
        if (ovc.nameSize) ov.style.setProperty('--jp-name-size', ovc.nameSize);
        if (ovc.nameLeft) ov.style.setProperty('--jp-name-left', ovc.nameLeft);
        if (ovc.numberTop) ov.style.setProperty('--jp-number-top', ovc.numberTop);
        if (ovc.numberSize) ov.style.setProperty('--jp-number-size', ovc.numberSize);
        if (ovc.numberLeft) ov.style.setProperty('--jp-number-left', ovc.numberLeft);
      } else {
        if (ovc.frontNumberTop) ov.style.setProperty('--jp-front-number-top', ovc.frontNumberTop);
        if (ovc.frontNumberSize) ov.style.setProperty('--jp-front-number-size', ovc.frontNumberSize);
        if (ovc.frontNumberLeft) ov.style.setProperty('--jp-front-number-left', ovc.frontNumberLeft);
      }
      host.style.position = host.style.position || 'relative';
      host.appendChild(ov);

      var zimg = isThumb ? null : host.querySelector('img');
      if (zimg && window.MutationObserver) {
        var syncZoom = function () {
          ov.style.transform = zimg.style.transform || '';
          ov.style.transformOrigin = zimg.style.transformOrigin || '';
        };
        new MutationObserver(syncZoom).observe(zimg, {
          attributes: true,
          attributeFilter: ['style'],
        });
        syncZoom();
      }
      return { ov: ov, name: nEl, number: numEl };
    }

    // Each side gets an overlay on its big slide and a scaled-down one on its thumb.
    function buildSide(item, side) {
      if (!item) return [];
      var out = [buildOverlay(slideHost(item), side, false)];
      var thumb = thumbHost(item);
      if (thumb) out.push(buildOverlay(thumb, side, true));
      return out;
    }
    var backOvs = buildSide(backItem, 'back');
    var frontOvs = buildSide(frontItem, 'front');

    function selectBack() {
      try {
        if (window.Flickity) {
          var f = window.Flickity.data(track);
          if (f && f.cells) {
            for (var i = 0; i < f.cells.length; i++) {
              if (f.cells[i].element === backItem) {
                f.select(i);
                return;
              }
            }
          }
        }
      } catch (e) { }
      var thumb = scope.querySelector('.evg-thumb[data-image="' + backItem.id + '"]');
      if (thumb) thumb.click();
    }

    // Whichever face THIS jersey prints in (retro keeps the old bubble font — see
    // .jp-retro in the CSS). The gate in opticalShiftEm must name the font actually
    // rendered, otherwise it reads "not loaded" forever and the shift silently stays 0.
    var printFont = retro ? "1em 'EVZ Retro Bubble'" : "1em 'Beachwood Tall Semibold'";
    // Optical centring: a geometrically-centred number can still look off-centre because
    // its ink weight is uneven (e.g. "14" leans right, "88" is balanced). Returns the shift
    // in EM (font-relative) so the number's ink centre-of-mass — not its bounding box — is
    // centred. EM (not px) is deliberate: the back image is often still off-screen in the
    // carousel when this runs, so a live pixel-width measurement would read 0; em derives
    // purely from the offscreen canvas and resolves correctly once the element is painted.
    var _octx = null;
    function opticalShiftEm(el, text) {
      if (!text) return 0;
      try {
        if (document.fonts && !document.fonts.check(printFont)) return 0;
        if (!_octx) {
          var oc = document.createElement('canvas');
          oc.width = 1024; oc.height = 512;
          _octx = oc.getContext('2d', { willReadFrequently: true });
        }
        var cs = getComputedStyle(el);
        var SIZE = 256;
        _octx.clearRect(0, 0, 1024, 512);
        _octx.textBaseline = 'middle';
        _octx.textAlign = 'center';
        _octx.font = (cs.fontWeight || '400') + ' ' + SIZE + 'px ' + (cs.fontFamily || 'sans-serif');
        _octx.fillStyle = '#000';
        _octx.fillText(text, 512, 256);
        var d = _octx.getImageData(0, 0, 1024, 512).data;
        var minX = 1e9, maxX = -1, sum = 0, wsum = 0;
        for (var y = 0; y < 512; y++) {
          var row = y * 1024;
          for (var x = 0; x < 1024; x++) {
            var a = d[(row + x) * 4 + 3];
            if (a > 40) { if (x < minX) minX = x; if (x > maxX) maxX = x; sum += a; wsum += a * x; }
          }
        }
        if (maxX < 0 || !sum) return 0;
        var bbox = (minX + maxX) / 2, com = wsum / sum, inkW = (maxX - minX) || 1;
        var frac = (com - bbox) / inkW; // + => ink mass leans right
        var inkEm = inkW / SIZE;        // ink width in em (canvas drawn at SIZE px)
        // Only ever nudge LEFT. Right-heavy numbers (e.g. 14) look shifted right and need
        // pulling left; left-heavy numbers (e.g. 37) look fine geometrically centred and
        // pushing them right (as full mass-centring does) looks bad. So clamp to <= 0.
        return Math.min(0, -(frac * inkEm));
      } catch (e) { return 0; }
    }

    var _lastNumber = '', _lastActive = false;
    // The var is set on the element itself, so front and back can share the name.
    function applyNumberOptical(el) {
      if (!el) return;
      el.style.setProperty('--jp-number-optical', (_lastActive ? opticalShiftEm(el, _lastNumber).toFixed(4) : '0') + 'em');
      // The bubble font loads on demand (font-display:swap), so opticalShiftEm returns 0
      // until the real glyphs exist. Recompute for the CURRENT number once the font
      // resolves — document.fonts.load is idempotent (resolves immediately if already loaded).
      if (_lastActive && document.fonts && document.fonts.load) {
        var n = _lastNumber;
        document.fonts.load(printFont).then(function () {
          if (n === _lastNumber && _lastActive) {
            el.style.setProperty('--jp-number-optical', opticalShiftEm(el, n).toFixed(4) + 'em');
          }
        }).catch(function () { });
      }
    }

    // Retro only: the bubble face runs far wider per glyph than the Beachwood the other
    // jerseys use, so a name like "MOVERARE" overran the jersey body. This block has no
    // length-step classes at all, so without this the name never shrank. Measure as drawn
    // and scale down to fit; cap is 1.10x the crest circle on the retro back photo
    // (circle measured at 30.2% of photo width => 33%). Non-retro never enters here, so
    // --jp-name-fit stays unset and the CSS `* var(--jp-name-fit, 1)` changes nothing.
    var RETRO_NAME_MAX = 0.33;
    function fitRetroName(el) {
      if (!retro || !el) return;
      el.style.removeProperty('--jp-name-fit');
      if (!el.textContent) return;
      var host = el.parentNode ? el.parentNode.getBoundingClientRect().width : 0;
      if (!host) return; // hidden overlay measures 0 — refitted when it is shown
      var rng = document.createRange();
      rng.selectNodeContents(el);
      var w = rng.getBoundingClientRect().width;
      if (w > host * RETRO_NAME_MAX) {
        el.style.setProperty('--jp-name-fit', ((host * RETRO_NAME_MAX) / w).toFixed(4));
      }
      // font-display:swap: the first measure can land on the fallback face. check() is
      // true once the real glyphs exist, so this cannot loop.
      if (document.fonts && document.fonts.load && !document.fonts.check(printFont)) {
        document.fonts.load(printFont).then(function () { fitRetroName(el); }).catch(function () { });
      }
    }

    return {
      update: function (name, number, active) {
        _lastNumber = number || '';
        _lastActive = !!active;
        backOvs.forEach(function (o) {
          o.name.textContent = name || '';
          o.number.textContent = number || '';
          o.ov.classList.toggle('jp-pv-on', !!active);
          applyNumberOptical(o.number);
          fitRetroName(o.name); // after the class toggle, so a shown overlay measures non-zero
        });
        // Front carries the number only, so it stays hidden for a name-only order.
        frontOvs.forEach(function (o) {
          o.number.textContent = number || '';
          o.ov.classList.toggle('jp-pv-on', !!(active && number));
          applyNumberOptical(o.number);
        });
        if (active) selectBack();
      },
    };
  }

  function initAll() {
    var blocks = document.querySelectorAll('[data-jp-block]');
    for (var i = 0; i < blocks.length; i++) init(blocks[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
